import { FormBlock } from "../formBlock/FormBlock";
import InputBox from "../inputBox/InputBox";
import { Calendar } from "../calendar/Calendar";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import styles from "./dateSelect.module.scss";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { createPortal } from "react-dom";

import { useState, useRef, forwardRef, useEffect, useMemo, useCallback } from "react";

interface Props {
  selectType: "singleDate" | "dateRange";
  timeSelect?: boolean;
  title?: string;
  typeStr?: string;
  titleDesc?: string;
  etc?: string;
  initialDate?: {
    date: {
      start: string | null;
      end?: string | null;
      format: string;
    };
  };
  minDate?: Date | string;
  extraComponent?: React.ReactNode;
  showErrorBorder?: boolean;
  showSuccessBorder?: boolean;
  showErrorBorderTime?: boolean;
  showSuccessBorderTime?: boolean;
  status?: "error" | "success" | "";
  onDateComplete?: ({
    date,
    time,
  }: {
    date: { start: string | null; end: string | null };
    time: { start: string | null; end: string | null };
  }) => void;
  disabled?: boolean;
}

export const DateSelect = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      selectType,
      timeSelect = false,
      title,
      typeStr,
      titleDesc,
      etc,
      initialDate,
      minDate,
      extraComponent,
      showErrorBorder = false,
      showSuccessBorder = false,
      showErrorBorderTime = false,
      showSuccessBorderTime = false,
      status = "",
      onDateComplete,
      disabled = false,
    }: Props,
    ref,
  ) => {
    // 플러그인 추가
    dayjs.extend(customParseFormat);

    // dayjs locale 설정
    dayjs.locale("ko");

    // 기본값
    const [defaultDate, setDefaultDate] = useState<{
      start: Date | null;
      end: Date | null;
    } | null>(null);

    useEffect(() => {
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      if (initialDate?.date?.start) {
        const dateString = initialDate.date.start;
        const format = initialDate.date.format;
        const formattedDate = dayjs(dateString, format, "ko").toDate();
        startDate = formattedDate;
      }
      if (initialDate?.date?.end) {
        const dateString = initialDate.date.end;
        const format = initialDate.date.format;
        const formattedDate = dayjs(dateString, format, "ko").toDate();
        endDate = formattedDate;
      }
      setDefaultDate({
        start: startDate,
        end: endDate,
      });

      setCalendarSelectedDate(
        selectType === "dateRange"
          ? defaultDate?.start && defaultDate?.end
            ? [defaultDate.start, defaultDate.end]
            : null
          : (defaultDate?.start ?? new Date()),
      );

      selectedDateRangeRef.current = {
        start: startDate ? dayjs(startDate).format("YYYY.MM.DD (ddd)") : null,
        end: endDate ? dayjs(endDate).format("YYYY.MM.DD (ddd)") : null,
      };
      selectedTimeRangeRef.current = {
        start: startDate ? dayjs(startDate).format("HH:mm") : null,
        end: endDate ? dayjs(endDate).format("HH:mm") : null,
      };
    }, [initialDate]);

    // 달력 오픈 상태
    const [calendarIsOpen, setCalendarIsOpen] = useState(false);
    const [timeSelectIsOpen, setTimeSelectIsOpen] = useState(false);

    // 달력 선택 카운트 참조
    const dateSelectCount = useRef(0);
    const dateSelectCount2 = useRef(0);

    // 선택된 날짜 최종 완료 플래그
    const [onCompleteFlag, setOnCompleteFlag] = useState(false);

    // 선택된 날짜(단일)
    const [selectedDate, setSelectedDate] = useState<string>("");

    // 선택된 날짜 범위
    const [selectedDateRange, setSelectedDateRange] = useState<{
      start: string | null;
      end: string | null;
    } | null>(null);

    // 선택된 날짜 범위 ref
    const selectedDateRangeRef = useRef<{
      start: string | null;
      end: string | null;
    } | null>(null);

    // 시간 선택
    const timeInputRef = useRef<HTMLInputElement>(null);

    // 선택된 시간(단일)
    const [selectedTime, setSelectedTime] = useState<string>("");

    // 선택된 시간 범위
    const [selectedTimeRange, setSelectedTimeRange] = useState<{
      start: string | null;
      end: string | null;
    } | null>(null);

    // 선택된 시간 범위 ref
    const selectedTimeRangeRef = useRef<{
      start: string | null;
      end: string | null;
    } | null>(null);

    // 날짜 선택 핸들러
    const onClickDayHandler = useCallback(
      (date: Date, event: () => void) => {
        const timeStamp = date.getTime();
        if (!selectType) return;
        setSelectedDate(dayjs(timeStamp).format("YYYY.MM.DD (ddd)")); // 시간 선택 모드일 경우
        if (timeSelect && selectType === "dateRange") {
          if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
            timeInputRef.current?.focus();
          } else {
            timeInputRef.current?.click();
          }
        }
        if (selectType === "singleDate") {
          setCalendarIsOpen(false);
          if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
            timeInputRef.current?.focus();
          } else {
            timeInputRef.current?.click();
          }
        }
      },
      [timeSelect, selectType],
    );

    // 날짜 범위 선택 핸들러
    const selectedDateRangeHandler = useCallback((date: string) => {
      if (dateSelectCount.current === 0) {
        selectedDateRangeRef.current = {
          start: date,
          end: null,
        };
        dateSelectCount.current += 1;
      } else if (dateSelectCount.current !== 0) {
        if (selectedDateRangeRef?.current && selectedDateRangeRef?.current?.start) {
          const startDateTime = new Date(`${selectedDateRangeRef.current.start} 00:00`).getTime();
          const endDateTime = new Date(`${date} 00:00`).getTime();
          if (startDateTime > endDateTime) {
            selectedDateRangeRef.current = {
              start: date,
              end: selectedDateRangeRef.current.start,
            };
          } else {
            selectedDateRangeRef.current = {
              ...selectedDateRangeRef.current,
              end: date,
            };
          }
          setSelectedDateRange(selectedDateRangeRef.current);
        }

        dateSelectCount.current = 0;
      }
    }, []);

    // 날짜 선택 완료 핸들러
    useEffect(() => {
      if (selectedDate) {
        if (selectType === "singleDate") {
          selectedDateRangeRef.current = {
            start: selectedDate,
            end: null,
          };
          setSelectedDateRange(selectedDateRangeRef.current);
          if (!timeSelect) {
            setOnCompleteFlag(true);
          } else {
            if (selectedTimeRangeRef?.current?.start) {
              setOnCompleteFlag(true);
            }
          }
        } else {
          if (selectType === "dateRange") {
            selectedDateRangeHandler(selectedDate);
          }
        }
        setSelectedDate("");
      }
    }, [selectedDate]);

    // 날짜 범위 선택 완료 핸들러
    useEffect(() => {
      if (
        selectType === "dateRange" &&
        selectedDateRange &&
        selectedDateRange?.start &&
        selectedDateRange?.end
      ) {
        if (!timeSelect) {
          setOnCompleteFlag(true);
          setCalendarIsOpen(false);
        }
      }
    }, [selectedDateRange]);

    // 시간 범위 선택 핸들러
    const timeRangeSelectHandler = useCallback((value: string) => {
      if (dateSelectCount2.current === 0) {
        selectedTimeRangeRef.current = {
          start: value,
          end: null,
        };
        dateSelectCount2.current += 1;
      } else if (dateSelectCount2.current !== 0) {
        if (
          selectedDateRangeRef?.current &&
          selectedDateRangeRef?.current?.start &&
          selectedTimeRangeRef?.current &&
          selectedTimeRangeRef?.current?.start
        ) {
          const startDateTime = new Date(
            `${selectedDateRangeRef?.current?.start ? selectedDateRangeRef.current.start : new Date()} ${selectedTimeRangeRef.current.start}`,
          ).getTime();
          const endDateTime = new Date(
            `${selectedDateRangeRef?.current?.end ? selectedDateRangeRef.current.end : new Date()} ${value}`,
          ).getTime();
          if (startDateTime > endDateTime) {
            selectedTimeRangeRef.current = {
              start: value,
              end: selectedTimeRangeRef.current.start,
            };
          } else {
            selectedTimeRangeRef.current = {
              ...selectedTimeRangeRef.current,
              end: value,
            };
          }
          setSelectedTimeRange(selectedTimeRangeRef.current);
          setOnCompleteFlag(true);
        }

        dateSelectCount2.current = 0;
      }
    }, []);

    // 시간 선택 완료 핸들러
    useEffect(() => {
      if (selectedTime && timeSelect) {
        if (selectType === "singleDate") {
          selectedTimeRangeRef.current = {
            start: selectedTime,
            end: null,
          };
          setSelectedTimeRange(selectedTimeRangeRef.current);
          setOnCompleteFlag(true);
        } else {
          if (selectType === "dateRange") {
            timeRangeSelectHandler(selectedTime);
          }
        }
      }
    }, [selectedTime]);

    // 시간 범위 선택 완료
    useEffect(() => {
      if (selectedTimeRange) {
        if (
          selectType === "dateRange" &&
          selectedTimeRange &&
          selectedTimeRange?.start &&
          selectedTimeRange?.end
        ) {
          setOnCompleteFlag(true);
          setCalendarIsOpen(false);
        }
      }
    }, [selectedTimeRange]);

    // 최종 날짜 선택 완료
    useEffect(() => {
      if (onCompleteFlag) {
        const result = {
          date: {
            start: selectedDateRangeRef?.current?.start || null,
            end: selectedDateRangeRef?.current?.end || null,
          },
          time: {
            start: selectedTimeRangeRef?.current?.start || null,
            end: selectedTimeRangeRef?.current?.end || null,
          },
        };
        setOnCompleteFlag(false);
        onDateComplete && onDateComplete(result);
      }
    }, [onCompleteFlag]);

    const TimeSelectComponent = useMemo(() => {
      return (
        <div className={styles["hidden"]}>
          <input
            type="time"
            // placeholder={`${now.format('HH:MM')}`}
            placeholder="00:00"
            ref={timeInputRef}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) return;
              if (!e.target.value) return;
              setSelectedTime(e.target.value);
              setTimeSelectIsOpen(false);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              if (!navigator.userAgent.match(/(iPod|iPhone|iPad)/)) return;
              if (!e.target.value) return;
              setSelectedTime(e.target.value);
              setTimeSelectIsOpen(false);
            }}
            tabIndex={-1}
            aria-hidden="true"
            disabled={disabled}
          />
        </div>
      );
    }, [selectedTime, defaultDate]);

    const [calendarSelectedDate, setCalendarSelectedDate] = useState<Date | Date[] | null>(null);

    return (
      <>
        <FormBlock
          title={title}
          typeStr={typeStr}
          titleDesc={titleDesc}
          etc={etc}
          content={
            <>
              <div
                className={`${styles["date-select-content"]} ${styles[selectType]} ${styles[timeSelect ? "time-select" : "not-time-select"]} ${styles[calendarIsOpen ? "open" : ""]}`}
              >
                <InputBox
                  type={"fakeText"}
                  // placeholder={`${now.format(!(timeSelect && selectType === 'dateRange') ? 'YYYY.MM.DD (ddd)' : 'YYYY.MM.DD (ddd) HH:mm')}`}
                  placeholder={`${!(timeSelect && selectType === "dateRange") ? "0000.00.00 (요일)" : "0000.00.00 (요일) 00:00"}`}
                  onClick={() => {
                    setCalendarIsOpen(!calendarIsOpen);
                  }}
                  value={`${selectedDateRange?.start ? selectedDateRange?.start : defaultDate?.start ? dayjs(defaultDate.start).format(!(timeSelect && selectType === "dateRange") ? "YYYY.MM.DD (ddd)" : "YYYY.MM.DD (ddd) A h:mm") : ""}${selectType === "dateRange" && selectedTimeRange?.start ? " " + selectedTimeRange?.start : ""}`}
                  showErrorBorder={showErrorBorder}
                  showSuccessBorder={showSuccessBorder}
                  showHelpTextError={false}
                  showHelpTextSuccess={false}
                  resultObj={{
                    result: status,
                    message: "날짜를 입력해주세요", // 메세지 없으면 border 안나오도록 되어있어서 임의로 메세지 추가
                  }}
                  aria-haspopup="dialog"
                  aria-expanded={calendarIsOpen}
                  disabled={disabled}
                />
                {selectType === "dateRange" && (
                  <InputBox
                    type={"fakeText"}
                    // placeholder={`${now.format(!(timeSelect && selectType === 'dateRange') ? 'YYYY.MM.DD (ddd)' : 'YYYY.MM.DD (ddd) HH:mm')}`}
                    placeholder={`${!(timeSelect && selectType === "dateRange") ? "0000.00.00 (요일)" : "0000.00.00 (요일) 00:00"}`}
                    onClick={() => {
                      setCalendarIsOpen(!calendarIsOpen);
                    }}
                    value={`${selectedDateRange?.end ? selectedDateRange?.end : defaultDate?.end ? dayjs(defaultDate.end).format(!(timeSelect && selectType === "dateRange") ? "YYYY.MM.DD (ddd)" : "YYYY.MM.DD (ddd) A h:mm") : ""}${selectType === "dateRange" && selectedTimeRange?.end ? " " + selectedTimeRange?.end : ""}`}
                    showErrorBorder={showErrorBorder}
                    showSuccessBorder={showSuccessBorder}
                    showHelpTextError={false}
                    showHelpTextSuccess={false}
                    resultObj={{
                      result: status,
                      message: "날짜를 입력해주세요", // 메세지 없으면 border 안나오도록 되어있어서 임의로 메세지 추가
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={calendarIsOpen}
                    disabled={disabled}
                  />
                )}
                {timeSelect && selectType === "singleDate" && (
                  <div className={styles["date-select-time"]}>
                    <InputBox
                      type="fakeText"
                      // placeholder={`${now.format('A h:mm')}`}
                      placeholder="00:00"
                      onClick={() => {
                        //ios -> focus, android -> click
                        if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
                          timeInputRef.current?.focus();
                        } else {
                          timeInputRef.current?.click();
                        }
                        setTimeSelectIsOpen(true);
                      }}
                      value={
                        selectedTimeRange?.start
                          ? dayjs("2025-04-08 " + selectedTimeRange.start).format("A h:mm")
                          : defaultDate?.start
                            ? dayjs(defaultDate.start).format("A h:mm")
                            : ""
                      }
                      showHelpTextError={false}
                      showHelpTextSuccess={false}
                      showSuccessBorder={showSuccessBorderTime}
                      showErrorBorder={showErrorBorderTime}
                      resultObj={{
                        result: status,
                        message: "시간을 입력해주세요", // 메세지 없으면 border 안나오도록 되어있어서 임의로 메세지 추가
                      }}
                      aria-haspopup="dialog"
                      aria-expanded={timeSelectIsOpen}
                      disabled={disabled}
                    />
                    {timeSelect && selectType === "singleDate" && TimeSelectComponent}
                  </div>
                )}
              </div>
              {extraComponent && extraComponent}
            </>
          }
        />
        {typeof window !== "undefined" &&
          createPortal(
            <div className={`${styles["date-select-calendar"]}`}>
              <>
                <div
                  className={`${styles["calendar-overlay"]} ${calendarIsOpen ? "open" : ""}`}
                  onClick={() => setCalendarIsOpen(false)}
                ></div>
                <Calendar
                  className={`${styles["calendar-wrap"]} ${calendarIsOpen ? "open" : ""}`}
                  value={calendarSelectedDate}
                  onChange={(date: Date) => {
                    setCalendarSelectedDate(date);
                  }}
                  minDate={minDate}
                  returnValue={selectType === "dateRange" ? "range" : "start"}
                  onClickDay={onClickDayHandler}
                  selectRange={selectType === "dateRange"}
                />

                {selectType === "dateRange" && timeSelect && TimeSelectComponent}
              </>
            </div>,
            document.body,
          )}
      </>
    );
  },
);
